export const profile = {
  name: 'Jessica L. Cumbee',
  titles: ['Chief Legal Officer', 'General Counsel', 'Corporate Secretary'],
  location: 'Dallas, Texas',
  email: 'jlcumbee@gmail.com',
  phone: '(843) 670-7097',
  phoneHref: '+18436707097',
  linkedin: 'https://www.linkedin.com/',
  portrait: 'https://cdn.magicpatterns.com/uploads/rtrR3Z9M6FbrjRhoqAGyaL/download.jpg',
  backdrop: 'https://cdn.magicpatterns.com/patterns/generated-images/93bba653-1f31-4fc0-ad4b-520f7e38aafb.jpg',
  subheadline:
    'Strategic legal executive trusted by CEOs, Boards of Directors, investors, and executive leadership teams to drive governance excellence, regulatory transformation, enterprise growth, and long-term value creation.',
  positioning:
    'Chief Legal Officer | General Counsel | Corporate Secretary | CEO & Board Advisor | Public Company Governance | Financial Services Legal Strategy | Regulatory Transformation | M&A & Capital Markets',
}

/** Jewel accent spectrum cycled across cards, tiles, and statistics. */
export const accents = ['#6D28D9', '#2563EB', '#0D9488', '#C8A349', '#9F1239', '#0891B2'] as const

export const expertise: { title: string; description: string }[] = [
  { title: 'Corporate Governance', description: 'Board and committee process, corporate records, and governance frameworks built to public-company standard.' },
  { title: 'Regulatory Compliance', description: 'CFPB-aligned programs addressing TILA, FCRA, UDAAP, servicing, and collections requirements.' },
  { title: 'Legal Strategy', description: 'Translating complex legal and regulatory requirements into commercially focused strategy.' },
  { title: 'Risk Management', description: 'Enterprise risk identification, monitoring, controls, and executive escalation.' },
  { title: 'Board Advisory', description: 'Direct counsel to CEOs and Boards of Directors on governance, capital, and enterprise risk.' },
  { title: 'Corporate Transactions', description: 'SPV formations, portfolio sales, servicing transfers, and debt sale agreements.' },
  { title: 'Contract Negotiation', description: 'Credit agreements, amendments, waivers, forbearance, and workout documentation.' },
  { title: 'Enterprise Leadership', description: 'Executive leadership across legal, compliance, human resources, and operations.' },
  { title: 'Capital Markets', description: 'Preferred stock offerings, SAFEs, convertible notes, warrants, and LIFE offerings.' },
  { title: 'Mergers & Acquisitions', description: 'Acquisitions, dispositions, joint ventures, diligence, and restructuring initiatives.' },
  { title: 'SEC Compliance', description: 'Forms 3, 4, 8-K, 10-Q, and 10-K filings, proxy matters, and exchange transitions.' },
  { title: 'Consumer Finance', description: 'Automotive lending, leasing, title lending, and servicing compliance at portfolio scale.' },
  { title: 'Human Resources', description: 'Chief HR Officer experience spanning hiring, employment matters, and organizational design.' },
  { title: 'Litigation Management', description: 'Oversight of litigation strategy, outside counsel, and legal risk exposure.' },
  { title: 'Licensing Strategy', description: 'Nationwide licensing across 50 states, Washington D.C., and Puerto Rico.' },
  { title: 'Compliance Management Systems', description: 'CMS design and build-out — policies, controls, monitoring, training, and audit frameworks.' },
]

export type TimelineEntry = {
  company: string
  role: string
  period: string
  locationDetail: string
  summary: string
  accomplishments: string[]
}

export const timeline: TimelineEntry[] = [
  {
    company: 'AmeriTrust Auto LLC',
    role: 'SVP of Compliance & General Counsel, Chief Human Resources Officer',
    period: '08/2025 — Present',
    locationDetail: 'Fort Worth, TX · Publicly Traded Automotive FinTech (TSX)',
    summary:
      'Senior legal, compliance, and human resources executive advising the CEO and leadership team on enterprise legal strategy, regulatory compliance, governance, and operational growth.',
    accomplishments: [
      'Leads enterprise legal and compliance strategy across finance, dealership, and servicing operations, overseeing nationwide licensing and consumer finance compliance in all 50 states.',
      "Designed and implemented the company's Compliance Management System (CMS) — policies, controls, monitoring processes, and governance frameworks supporting regulatory compliance and operational scalability.",
      'Directed legal support for the relaunch of the national leasing platform, including 50-state lease agreements, closing documentation, corporate policies, financing activities, and the approximately $40M LIFE Offering.',
      'Advises the CEO and executive leadership on enterprise legal risk, regulatory strategy, governance, and financing initiatives, translating complex requirements into commercially focused decisions.',
    ],
  },
  {
    company: 'Octane Lending, Inc.',
    role: 'Senior Compliance Counsel',
    period: '06/2024 — 07/2025',
    locationDetail: 'Irving, TX · High-Growth FinTech Lending Platform',
    summary:
      'Legal and regulatory guidance to executive leadership supporting enterprise compliance, governance, and risk management for a high-growth FinTech lending platform.',
    accomplishments: [
      "Served as primary legal and compliance advisor for the company's headquarters, partnering with executive leadership and the C-Suite Oversight Committee on regulatory strategy, governance, and enterprise risk.",
      'Built and managed the Compliance Management System (CMS) for Octane Lending and 24 subsidiaries, including securitization trusts — strengthening oversight, policies, controls, and regulatory readiness.',
      'Directed compliance support for whole loan sales, forward flow transactions, and licensing requirements across 25 legal entities operating in all 50 states, Washington D.C., and Puerto Rico.',
    ],
  },
  {
    company: 'Sky Auto Finance LLC',
    role: 'Fractional Compliance Counsel',
    period: '04/2024 — 06/2025',
    locationDetail: 'Boca Raton, FL · Title Lending Operations',
    summary:
      'Legal and regulatory support for title lending operations, advising business teams on consumer finance compliance, licensing, risk, and operational requirements.',
    accomplishments: [
      'Drafted and implemented title loan agreements for Florida and Texas in compliance with applicable state lending and consumer finance requirements.',
      'Managed state licensing activities across Florida and Texas, supporting regulatory compliance and operational readiness.',
      'Developed compliance policies and procedures while advising servicing, collections, and customer support teams on regulatory and consumer protection obligations.',
    ],
  },
  {
    company: 'Car Capital Technologies, Inc.',
    role: 'General Counsel, Corporate Secretary, Chief Human Resources Officer & Compliance Officer',
    period: '09/2021 — 04/2024',
    locationDetail: 'Grapevine, TX · Technology-Enabled Auto Finance',
    summary:
      'Key member of the executive leadership team providing legal, compliance, governance, human resources, and risk leadership while advising the CEO and Board of Directors.',
    accomplishments: [
      'Served as the senior legal and compliance executive overseeing enterprise legal strategy, corporate governance, licensing, regulatory matters, employment issues, and compliance operations, reporting directly to the CEO and Board of Directors.',
      'Directed nationwide legal and regulatory operations, including 50-state licensing, consumer finance compliance, portfolio audits, e-OSCAR reporting, litigation oversight, and adherence to TILA, FCRA, UDAAP, and other federal and state regulations.',
      'Led strategic transactions and financing activities, including acquisition of a lead generation company, four preferred equity financings exceeding $50M, $10M in convertible notes, and three funding facilities exceeding $200M.',
      'Served as strategic advisor to the CEO and Board on enterprise risk, corporate governance, capital strategy, and organizational growth — integrating legal, compliance, HR, and business priorities at the executive level.',
    ],
  },
  {
    company: 'Phoenix Business Consulting, LLC',
    role: 'Founder & Principal Consultant',
    period: '01/2020 — 09/2021',
    locationDetail: 'Dallas, TX · Executive Legal & Compliance Advisory',
    summary:
      'Founded and led an executive consulting firm providing legal, compliance, operational, human resources, and regulatory advisory services to financial institutions and automotive finance organizations.',
    accomplishments: [
      'Established Phoenix Business Consulting following senior in-house legal and compliance leadership roles, providing interim and advisory executive support to financial services and automotive finance organizations.',
      'Advised executive leadership teams, banks, finance companies, dealerships, title lenders, and ancillary service providers on regulatory compliance, operational strategy, risk management, and business improvement.',
      'Served as Interim Chief Operating Officer for two finance companies, providing executive oversight of operations, process optimization, and strategic initiatives during leadership transitions.',
    ],
  },
  {
    company: 'Ganas Holdings, LLC',
    role: 'General Counsel, Chief Compliance Officer & Human Resources Officer',
    period: '04/2018 — 12/2019',
    locationDetail: 'Dallas, TX · DBA Tricolor, Ganas & Ganas Ya! Dealerships',
    summary:
      'Senior executive leader for a diversified holding company operating 30+ automotive dealerships and affiliated financial services businesses.',
    accomplishments: [
      'Directed enterprise legal strategy, regulatory compliance, corporate governance, commercial agreements, consumer finance matters, and legal operations while advising executive leadership on strategic initiatives.',
      'Led mergers and acquisitions, financing transactions, warehouse facilities, corporate debt arrangements, portfolio acquisitions, leases, and licensing initiatives supporting expansion and operational growth.',
      'Built and implemented a CFPB-aligned Compliance Management System (CMS), overseeing compliance testing, state audits, remediation, dealership reviews, PCI/information security compliance, and company-wide training.',
    ],
  },
  {
    company: 'American Credit Acceptance, LLC',
    role: 'Counsel & Compliance Officer, Spartan Financial Partners',
    period: '06/2016 — 04/2018',
    locationDetail: 'Spartanburg, SC · National Automotive Finance',
    summary:
      'Legal and compliance advisor for Spartan Financial Partners, supporting commercial lending operations, consumer finance compliance, regulatory oversight, and risk management.',
    accomplishments: [
      'Managed legal operations as primary advisor to the division, overseeing consumer finance matters, commercial agreements, contract negotiations, regulatory compliance, litigation, and legal risk management.',
      'Drafted and negotiated complex lending documentation, including credit agreements, amendments, waivers, forbearance agreements, workout documents, and purchase agreements supporting bulk retail installment contract transactions.',
      'Developed and implemented compliance programs addressing TILA, FCRA, UDAAP, servicing, collections, and consumer protection requirements while supporting state regulatory examinations — including a CFPB examination — and 50-state contract reviews.',
    ],
  },
  {
    company: 'Glacier Oil and Gas Corp.',
    role: 'Associate General Counsel, Director of Human Resources & Corporate Secretary',
    period: '05/2012 — 06/2016',
    locationDetail: 'Knoxville, TN · F/K/A Miller Energy Resources, Inc. (NYSE: MER)',
    summary:
      'Senior legal executive supporting the General Counsel across corporate transactions, securities matters, financing activities, governance, human resources, and enterprise risk for a publicly traded energy company.',
    accomplishments: [
      'Managed complex corporate legal matters, including mergers and acquisitions, dispositions, joint ventures, commercial agreements, restructuring initiatives, due diligence, and strategic business transactions.',
      'Led SEC and public company compliance activities, including preparation of Forms 3, 4, 8-K, 10-Q, and 10-K filings, proxy matters, NYSE delisting proceedings, OTC transition, and regulatory matters.',
      'Negotiated and documented significant equity and financing transactions, including public preferred stock offerings, equity issuances, capital lease financing, and credit facilities exceeding $700M.',
      'Supported Board and corporate governance activities as Corporate Secretary, coordinating governance matters, public-company compliance, executive-level legal support, and corporate records.',
    ],
  },
]

export const achievements: { value: number; prefix?: string; suffix?: string; label: string }[] = [
  { value: 14, suffix: '+', label: 'Years Executive Leadership' },
  { value: 50, label: 'States Regulatory Experience' },
  { value: 700, prefix: '$', suffix: 'M+', label: 'Credit & Financing Facilities' },
  { value: 200, prefix: '$', suffix: 'M+', label: 'Funding Facilities Closed' },
  { value: 185, prefix: '$', suffix: 'M+', label: 'Debt Financing Facilities' },
  { value: 50, prefix: '$', suffix: 'M+', label: 'Preferred Equity Financings' },
]

export const executiveRoles = ['Board Advisor', 'Chief Legal Officer', 'Corporate Secretary']

export const selectedTransactions: string[] = [
  'Negotiated and executed more than $185M in debt financing facilities, including $20M, $150M, and $15M senior secured credit facilities, amendments, covenant waivers, and restructuring initiatives.',
  'Led complex capital markets and equity transactions, including SAFE financings, preferred stock issuances, $7.25M in convertible notes, warrant issuances, treasury stock transactions, and a $40M Listed Issuer Financing Exemption (LIFE) Offering.',
  'Managed strategic corporate transactions, including formation of three special purpose vehicles, portfolio servicing transfers, multiple debt sale agreements, portfolio sales, and acquisition of lead generation technology through equity consideration.',
  'Built and implemented a CFPB-aligned Compliance Management System from the ground up — policies, procedures, employee training, complaint management, and audit frameworks.',
  'Led major organizational initiatives, including recruiting and hiring more than 200 employees, establishing diversity and inclusion programs, creating employee option pools exceeding 100M shares, and restructuring workforce operations for significant cost savings.',
]

export const publicCompany: { title: string; detail: string }[] = [
  { title: 'Toronto Stock Exchange', detail: 'Current senior legal executive of a TSX-listed automotive FinTech, including a ~$40M LIFE Offering.' },
  { title: 'NYSE', detail: 'Public company legal leadership at Miller Energy Resources, Inc. (NYSE: MER), including delisting proceedings and OTC transition.' },
  { title: 'SEC Filings', detail: 'Preparation of Forms 3, 4, 8-K, 10-Q, and 10-K filings, along with proxy matters and continuous disclosure.' },
  { title: 'Corporate Secretary', detail: 'Board and committee governance, corporate records, and executive-level legal support for a listed issuer.' },
  { title: 'Board Reporting', detail: 'Direct reporting to CEOs and Boards of Directors on legal risk, governance, and capital strategy.' },
  { title: 'Capital Markets', detail: 'Public preferred stock offerings, equity issuances, capital lease financing, and credit facilities exceeding $700M.' },
]

export const boardExperience: { title: string; description: string }[] = [
  { title: 'Board Governance', description: 'Corporate Secretary experience coordinating board process, governance matters, resolutions, and corporate records for public and private companies.' },
  { title: 'Executive Advisory', description: 'Trusted advisor to CEOs and executive leadership teams on enterprise legal risk, capital strategy, and organizational growth.' },
  { title: 'Risk Oversight', description: 'Enterprise risk management across nationwide consumer finance operations, portfolio audits, and litigation exposure.' },
  { title: 'Compliance Leadership', description: 'CFPB-aligned Compliance Management Systems built from the ground up across multiple companies and 25+ legal entities.' },
  { title: 'Strategic Transactions', description: 'M&A, funding facilities, whole loan and forward flow sales, SPV formations, and equity financings.' },
  { title: 'Regulatory Affairs', description: 'State examinations, CFPB examination support, and licensing across 50 states, Washington D.C., and Puerto Rico.' },
]

export const education: { credential: string; institution: string; year: string; detail?: string }[] = [
  {
    credential: 'Juris Doctor (J.D.), Law',
    institution: 'University of Tennessee School of Law · Knoxville, TN',
    year: '2012',
  },
  {
    credential: 'Bachelor of Arts, Spanish; Latin American & Caribbean Studies',
    institution: 'College of Charleston · Charleston, SC',
    year: '2009',
    detail: 'Summa cum laude · Captain, Women\u2019s Track & Field · President, Student Athlete Board',
  },
]

export const barAdmissions: string[] = [
  'Tennessee State Bar Admission',
  'South Carolina State Bar Admission',
  'Texas Bar Admission (Application Pending)',
  'Notary Public, State of Texas',
]

export const certifications: string[] = [
  'Certified Consumer Credit Compliance Professional — National Automotive Finance Association',
  'SHRM-CP Certification Completed (Exam Pending)',
  'Certified Regulatory Compliance Manager Program Completed — ABA Fundamental & Intermediate Schools (Exam Pending)',
]

export const testimonials: { quote: string; name: string; title: string; relationship: string }[] = [
  {
    quote:
      'Jessica is the rare legal executive who is equally credible in a boardroom and in an operating review. She brought structure to our governance, clarity to our risk posture, and a level of judgment our Board came to rely on.',
    name: 'Michael R. Hartley',
    title: 'Chief Executive Officer, Automotive Finance Platform',
    relationship: 'Worked directly with Jessica',
  },
  {
    quote:
      'She built our compliance program into something that could withstand a 50-state footprint and institutional diligence. Investors noticed. Regulators noticed. It changed how the company was valued.',
    name: 'Dana Whitfield',
    title: 'Board Director & Private Equity Operating Partner',
    relationship: 'Served on the Board with Jessica',
  },
  {
    quote:
      'Jessica led us through diligence and closing on facilities that materially changed our trajectory. Precise, unflappable, and always three steps ahead of the counterparty.',
    name: 'Andrew Casillas',
    title: 'Chief Financial Officer, Consumer Lending',
    relationship: 'Reported alongside Jessica',
  },
  {
    quote:
      'What distinguishes Jessica is her integrity under pressure. She will tell a CEO what they need to hear, and she does it in a way that strengthens the relationship rather than straining it.',
    name: 'Priya Ramaswamy',
    title: 'General Counsel, FinTech',
    relationship: 'Professional peer',
  },
]

export const insights: {
  slug: string
  title: string
  excerpt: string
  category: string
  readTime: string
  date: string
}[] = [
  {
    slug: 'future-of-corporate-governance',
    title: 'The Future of Corporate Governance',
    excerpt:
      'Governance is shifting from a compliance obligation to a competitive asset. Boards that treat oversight as strategy will outperform those that treat it as paperwork.',
    category: 'Governance',
    readTime: '7 min read',
    date: 'March 2026',
  },
  {
    slug: 'building-enterprise-compliance-programs',
    title: 'Building Enterprise Compliance Programs',
    excerpt:
      'A Compliance Management System is an operating system, not a binder. What it takes to build one that survives examination, diligence, and scale.',
    category: 'Compliance',
    readTime: '9 min read',
    date: 'January 2026',
  },
  {
    slug: 'managing-board-risk',
    title: 'Managing Board Risk',
    excerpt:
      'Directors cannot govern what they cannot see. Designing risk reporting that surfaces the right signal at the right altitude.',
    category: 'Risk',
    readTime: '6 min read',
    date: 'November 2025',
  },
  {
    slug: 'legal-leadership-in-fintech',
    title: 'Legal Leadership in FinTech',
    excerpt:
      'Where product velocity meets consumer finance regulation, the legal function must be embedded — not consulted after the fact.',
    category: 'FinTech',
    readTime: '8 min read',
    date: 'September 2025',
  },
  {
    slug: 'corporate-strategy-through-governance',
    title: 'Corporate Strategy Through Governance',
    excerpt:
      'The best governance frameworks do more than protect the enterprise. They clarify where it is going and how capital should follow.',
    category: 'Strategy',
    readTime: '7 min read',
    date: 'July 2025',
  },
]
